/* eslint-disable max-len */
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ActionButton from '../../../../components/ActionButton/ActionButton';
import LiteracyScreeningDiagnosticMeasuresPanel, {
  LiteracyScreeningDiagnosticsEditState,
} from '../../../../components/FormLayouts/LiteracyScreening/LiteracyScreeningDiagnosticMeasuresPanel';
import LiteracyScreeningLEASchoolInformationPanel, {
  formatLiteracyScreeningNameWithIdentifier,
} from '../../../../components/FormLayouts/LiteracyScreening/LiteracyScreeningLEASchoolInformationPanel';
import LiteracyScreeningReimbursementPanel, {
  LiteracyScreeningReimbursementEditState,
} from '../../../../components/FormLayouts/LiteracyScreening/LiteracyScreeningReimbursementPanel';
import LiteracyScreeningUploadDocuments from '../../../../components/FormLayouts/LiteracyScreening/LiteracyScreeningUploadDocuments';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { selectSelectedDistrict } from '../../../../redux/slices/appSlice';
import {
  selectLiteracyScreeningDiagnostics,
  selectLiteracyScreeningLEASchools,
  selectLiteracyScreeningLoadingDiagnostics,
  selectLiteracyScreeningLoadingFiles,
  selectLiteracyScreeningLoadingLEASchools,
  selectLiteracyScreeningLoadingOptionSets,
  selectLiteracyScreeningSchoolList,
  selectLiteracyScreeningSchoolYears,
  selectLiteracyScreeningUpdate,
  setLiteracyScreeningPendingSchool,
} from '../../../../redux/slices/literacyScreeningSlice';
import { createNotification } from '../../../../redux/slices/notificationSlice';
import {
  createLiteracyScreeningDiagnostics,
  fetchLiteracyScreeningDiagnostics,
  fetchLiteracyScreeningFilesByLeaThunk,
  fetchLiteracyScreeningLEASchools,
  fetchLiteracyScreeningOptionSets,
  fetchLiteracyScreeningUpdate,
  updateLiteracyScreeningDiagnostics,
} from '../../../../redux/thunks/literacyScreeningThunks';
import { abortPromiseOnUnmount } from '../../../../services/base.service';
import { appRoutePaths } from '../../../../services/route.service';
import {
  buildLiteracyScreeningDiagnosticsSaveModel,
  isNewLiteracyScreeningDiagnosticsId,
} from '../../../../types/LiteracyScreeningDiagnosticMeasuresModel';
import { LiteracyScreeningSchoolModel } from '../../../../types/LiteracyScreeningSchoolModel';
import { LiteracyScreeningDiagnosticMeasuresFormValues } from '../../../../types/Validation/LiteracyScreeningDiagnosticMeasuresValidationSchema';
import { LiteracyScreeningReimbursementFormValues } from '../../../../types/Validation/LiteracyScreeningReimbursementValidationSchema';
import DistrictSchoolInfoPage from '../DistrictSchoolInfoPage';
import './LiteracyScreeningInput.css';

const EMPTY_DIAGNOSTICS_VALUES: LiteracyScreeningDiagnosticMeasuresFormValues =
{
  phonemicAwareness: [],
  phonics: [],
  vocabulary: [],
  oralLanguage: [],
};

const EMPTY_REIMBURSEMENT_VALUES: LiteracyScreeningReimbursementFormValues = {
  reimbursement: '',
  explanation: '',
};

const LiteracyScreeningInput = (): ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { schoolYear } = useParams<{ schoolYear: string }>();
  const selectedDistrict = useAppSelector(selectSelectedDistrict);
  const leaSchools = useAppSelector(selectLiteracyScreeningLEASchools);
  const schoolList = useAppSelector(selectLiteracyScreeningSchoolList);
  const schoolYearOptions = useAppSelector(selectLiteracyScreeningSchoolYears);
  const isLoadingLEASchools = useAppSelector(
    selectLiteracyScreeningLoadingLEASchools
  );
  const isLoadingOptionSets = useAppSelector(
    selectLiteracyScreeningLoadingOptionSets
  );
  const isLoadingDiagnostics = useAppSelector(
    selectLiteracyScreeningLoadingDiagnostics
  );
  const isLoadingFiles = useAppSelector(selectLiteracyScreeningLoadingFiles);
  const diagnostics = useAppSelector(selectLiteracyScreeningDiagnostics);
  const literacyScreeningUpdate = useAppSelector(selectLiteracyScreeningUpdate);
  const [isSaving, setIsSaving] = useState(false);
  const [diagnosticsEditState, setDiagnosticsEditState] = useState({
    dirty: false,
    isValid: true,
  });
  const [reimbursementEditState, setReimbursementEditState] = useState({
    dirty: false,
    isValid: true,
    reimbursement: '',
  });
  const diagnosticsValuesRef = useRef(EMPTY_DIAGNOSTICS_VALUES);
  const reimbursementValuesRef = useRef(EMPTY_REIMBURSEMENT_VALUES);

  useEffect(() => {
    const optionSetsPromise: unknown = dispatch(
      fetchLiteracyScreeningOptionSets({
        module: 'LiteracyScreening',
        page: 'InputPage',
        include: 'Common',
      })
    );

    return () => {
      abortPromiseOnUnmount(optionSetsPromise);
    };
  }, [dispatch]);

  useEffect(() => {
    if (!schoolYear) {
      return;
    }

    const leaSchoolsPromise: unknown = dispatch(
      fetchLiteracyScreeningLEASchools(schoolYear)
    );
    const diagnosticsPromise: unknown = dispatch(
      fetchLiteracyScreeningDiagnostics({ schoolYear })
    );
    const literacyScreeningUpdatePromise: unknown = dispatch(
      fetchLiteracyScreeningUpdate(schoolYear)
    );

    return () => {
      abortPromiseOnUnmount(leaSchoolsPromise);
      abortPromiseOnUnmount(diagnosticsPromise);
      abortPromiseOnUnmount(literacyScreeningUpdatePromise);
    };
  }, [dispatch, schoolYear]);

  useEffect(() => {
    const leaId = leaSchools.leaIdentifier || selectedDistrict.districtId || '';
    if (!schoolYear || !leaId) {
      return;
    }

    const filesPromise: unknown = dispatch(
      fetchLiteracyScreeningFilesByLeaThunk({
        leaId,
        schoolYear,
      })
    );

    return () => {
      abortPromiseOnUnmount(filesPromise);
    };
  }, [
    dispatch,
    schoolYear,
    leaSchools.leaIdentifier,
    selectedDistrict.districtId,
  ]);

  const formatLongSchoolYear = (yearValue: string): string => {
    const year = Number(yearValue);
    if (!Number.isFinite(year) || year <= 0) {
      return yearValue;
    }
    return `${year - 1}-${year}`;
  };

  const schoolYearDisplay =
    schoolYearOptions.find((option) => option.value === schoolYear)?.text ||
    (schoolYear ? formatLongSchoolYear(schoolYear) : '');
  const leaNameDisplay = formatLiteracyScreeningNameWithIdentifier(
    leaSchools.districtName || selectedDistrict.name,
    leaSchools.leaIdentifier || selectedDistrict.districtId
  );

  const handleDiagnosticsEditStateChange = useCallback(
    (state: LiteracyScreeningDiagnosticsEditState): void => {
      diagnosticsValuesRef.current = state.values;
      setDiagnosticsEditState((previous) =>
        previous.dirty === state.dirty && previous.isValid === state.isValid
          ? previous
          : { dirty: state.dirty, isValid: state.isValid }
      );
    },
    []
  );

  const handleReimbursementEditStateChange = useCallback(
    (state: LiteracyScreeningReimbursementEditState): void => {
      reimbursementValuesRef.current = state.values;
      setReimbursementEditState((previous) =>
        previous.dirty === state.dirty &&
        previous.isValid === state.isValid &&
        previous.reimbursement === state.values.reimbursement
          ? previous
          : {
              dirty: state.dirty,
              isValid: state.isValid,
              reimbursement: state.values.reimbursement,
            }
      );
    },
    []
  );

  const hasChanges = diagnosticsEditState.dirty || reimbursementEditState.dirty;
  const isFormValid =
    diagnosticsEditState.isValid && reimbursementEditState.isValid;
  const isSaveDisabled =
    !literacyScreeningUpdate || !hasChanges || !isFormValid || isSaving;

  const saveTooltip = (): string => {
    if (!hasChanges) {
      return 'No changes have been made';
    }
    if (!isFormValid) {
      return 'Make sure all required fields are complete';
    }
    return 'Save Literacy Screening changes';
  };

  const handleCancelClick = (): void => {
    navigate(appRoutePaths.LiteracyScreeningList);
  };

  const handleSave = async (): Promise<void> => {
    if (isSaveDisabled) {
      return;
    }

    const payload = buildLiteracyScreeningDiagnosticsSaveModel(
      diagnostics,
      schoolYear || '',
      leaSchools.leaIdentifier || selectedDistrict.districtId || '',
      diagnosticsValuesRef.current,
      reimbursementValuesRef.current
    );

    setIsSaving(true);
    try {
      if (isNewLiteracyScreeningDiagnosticsId(payload.id)) {
        const createdId = await dispatch(
          createLiteracyScreeningDiagnostics(payload)
        ).unwrap();
        if (typeof createdId !== 'string' || createdId.trim() === '') {
          return;
        }
      } else {
        await dispatch(
          updateLiteracyScreeningDiagnostics({
            id: payload.id,
            model: payload,
          })
        ).unwrap();
      }

      if (schoolYear) {
        await dispatch(
          fetchLiteracyScreeningDiagnostics({
            schoolYear,
            showLoading: false,
          })
        ).unwrap();
      }

      dispatch(
        createNotification({
          severity: 'success',
          children: 'Literacy Screening data saved',
        })
      );
    } catch {
      // API errors are surfaced by the diagnostics thunks.
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditSchool = (school: LiteracyScreeningSchoolModel): void => {
    dispatch(setLiteracyScreeningPendingSchool(school));
    navigate(
      appRoutePaths.LiteracyScreeningSchoolInput(
        school.leaIdentifier ||
        leaSchools.leaIdentifier ||
        selectedDistrict.districtId,
        school.schoolIdentifier,
        schoolYear || '',
        school.id
      )
    );
  };

  return (
    <DistrictSchoolInfoPage
      pageClass="manual-input data-input--literacy-screening--input"
      loadingDataId="literacy-screening-input-loader"
      isLoading={
        isLoadingLEASchools ||
        isLoadingOptionSets ||
        isLoadingDiagnostics ||
        isLoadingFiles
      }
      backBarLocation={appRoutePaths.LiteracyScreeningList}
    >
      <div className="heading-button-error-container">
        <div className="manual-button-container">
          <span className="subheading" data-testid="component-heading-text">
            Literacy Screening
          </span>
          <div className="button-group">
            <p className="required-label">* Required Field</p>
            <ActionButton
              classes="button no-wrap-text cancel-button"
              onClick={handleCancelClick}
              dataTestId="cancel-button"
              tooltipText="Navigate to the last page"
            >
              <>Cancel</>
            </ActionButton>
            <ActionButton
              classes="button--secondary no-wrap-text submit-button"
              dataTestId="submit-button"
              disabled={isSaveDisabled}
              tooltipText={saveTooltip()}
              loading={isSaving}
              onClick={handleSave}
            >
              <>Save</>
            </ActionButton>
          </div>
        </div>
      </div>
      <LiteracyScreeningLEASchoolInformationPanel
        leaNameDisplay={leaNameDisplay}
        schoolYearDisplay={schoolYearDisplay}
        schoolList={schoolList}
        onEditSchool={handleEditSchool}
      />
      <LiteracyScreeningDiagnosticMeasuresPanel
        onEditStateChange={handleDiagnosticsEditStateChange}
      />
      <LiteracyScreeningReimbursementPanel
        onEditStateChange={handleReimbursementEditStateChange}
      />
      <LiteracyScreeningUploadDocuments
        schoolYear={schoolYear}
        leaIdentifier={leaSchools.leaIdentifier || selectedDistrict.districtId}
        reimbursement={
          reimbursementEditState.reimbursement ||
          diagnostics?.reimbursement ||
          ''
        }
      />
    </DistrictSchoolInfoPage>
  );
};

export default LiteracyScreeningInput;
